import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Table, Select, Checkbox } from 'antd';
import type { TableColumnsType, GetProp, TableProps } from 'antd';
import { Icons } from '../../assets';
import Highlighter from 'react-highlight-words';
import PromoutersHeaderInfo from './components/PromoutersHeaderInfo';
import { columnsPromoutersTable } from './components/columns';
import { useDispatch, useSelector } from 'react-redux';
import { authInfoSelect } from '../../entities/auth';
import { IBaseQuery } from '../../types';
import { AppDispatch } from '../../config/store';
import { getPromouters } from '../../api/thunks/booking';
import { bookingSelect } from '../../entities/booking';

type TablePaginationConfig = Exclude<
    GetProp<TableProps, 'pagination'>,
    boolean
>;

interface TableParams {
    pagination?: TablePaginationConfig;
    sortField?: string;
    sortOrder?: string;
    filters?: Parameters<GetProp<TableProps, 'onChange'>>[1];
}

moment().format();

const PromoutersPage = () => {
    const { shop } = useSelector(authInfoSelect);
    const { promouters, totalProm } = useSelector(bookingSelect);
    const [openedSettingsPopup, setOpenedSettingsPopup] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [loading, setLoading] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedClient, setSelectedClient] = useState<any>(undefined);
    const [searchText, setSearchText] = useState('');
    const [selectValue, setSelectValue] = useState<number>(1);

    const [tableParams, setTableParams] = useState<TableParams>({
        pagination: {
            current: 1,
            pageSize: 1,
        },
    });

    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        if (shop) {
            const { current, pageSize } = tableParams.pagination!;
            const request: IBaseQuery = {
                limit: pageSize!,
                offset: (current! - 1) * pageSize!,
            };

            dispatch(getPromouters({ shop_id: shop.id, params: request }));
            setLoading(false);
        }
    }, [loading, tableParams, shop, dispatch]);

    useEffect(() => {
        setLoading(true);
    }, []);

    const getColumnSearchProps = dataIndex => ({
        render: text =>
            searchText ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text.toString()}
                />
            ) : (
                text
            ),
    });

    const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
    };

    const optionsSelect = [
        { value: 1, label: 'Заблокировать / активировать' },
        { value: 2, label: 'Удалить' },
        { value: 3, label: 'Разрешить' },
        { value: 4, label: 'Отказать' },
        { value: 5, label: 'пометить в ожидание' },
    ];

    const onChangeSelect = value => {
        setSelectValue(value);
    };

    const renderTableFooter = () => {
        return (
            <FooterWrap>
                <ActionWrap>
                    <StyledSelect
                        options={optionsSelect}
                        value={selectValue}
                        onChange={onChangeSelect}
                    />
                    <StyledActionButton>
                        <span>Применить</span>
                    </StyledActionButton>
                    <StyledCheckbox>
                        <span>для всех</span>
                    </StyledCheckbox>
                </ActionWrap>
            </FooterWrap>
        );
    };

    const handleTableChange: TableProps['onChange'] = (
        pagination,
        filters,
        sorter
    ) => {
        setTableParams({
            pagination,
            filters,
            ...sorter,
        });
    };

    const overlayOnClick = () => {
        setOpenedSettingsPopup(false);
    };

    const handlerClickId = id => {
        const findClient = promouters?.find(o => o.id === id);
        if (findClient) {
            setSelectedClient(findClient);
            setOpenedSettingsPopup(true);
        }
    };

    const columns: TableColumnsType = columnsPromoutersTable(
        searchText,
        getColumnSearchProps,
        handlerClickId
    );

    const components = {
        body: {
            row: ({ children, props, ...restProps }) => {
                const filtered = promouters?.find(
                    o => o.id === restProps['data-row-key']
                );
                const highlight = filtered && filtered.shopStatus === 'request';
                return (
                    <tr
                        {...restProps}
                        className={
                            highlight
                                ? 'ant-table-row ant-table-row-level-0 highlight-row'
                                : 'ant-table-row ant-table-row-level-0'
                        }
                    >
                        {children}
                    </tr>
                );
            },
        },
    };

    return (
        <Wrapper>
            <Header>
                <HeaderTitle>Промоутеры</HeaderTitle>
            </Header>
            <PromoutersHeaderInfo />
            <SearchWrap>
                <SearchInput
                    placeholder="Поиск..."
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                />
                <ButtonBlok>
                    <StyledAddButton>
                        <img src={Icons.AddGreyPlusInCircle} alt="icon grey" />
                        <span>Добавить</span>
                    </StyledAddButton>
                </ButtonBlok>
            </SearchWrap>
            <TableWrapper>
                <Table
                    size="small"
                    rowSelection={rowSelection}
                    columns={columns}
                    rowKey={rec => rec.id}
                    dataSource={promouters}
                    onChange={handleTableChange}
                    pagination={{
                        current: tableParams.pagination?.current,
                        pageSize: tableParams.pagination?.pageSize,
                        total: totalProm,
                    }}
                    footer={renderTableFooter}
                    components={components}
                />
            </TableWrapper>
            <Overlay
                onClick={overlayOnClick}
                display={openedSettingsPopup ? 'block' : 'none'}
            />
        </Wrapper>
    );
};

export default PromoutersPage;

const StyledAddButton = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 3px 10px;
    gap: 10px;
    cursor: pointer;
    width: 128px;
    height: 34px;
    background: #ffffff;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.08);
    border-radius: 5px;
    flex: none;
    order: 0;
    flex-grow: 0;
    color: #8f8f8f;
    & svg {
        width: 20px;
        height: 20px;
        color: #8f8f8f;
    }
`;

const FooterWrap = styled.div`
    display: flex;
    flex-direction: raw;
`;

const ActionWrap = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 10px;
`;

const StyledSelect = styled(Select)`
    box-sizing: border-box;
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 10px;

    width: 175px;
    height: 38px;
`;
const StyledActionButton = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 5px 10px;
    gap: 10px;

    width: 98px;
    height: 38px;
    background: #a7388e;
    border-radius: 5px;

    cursor: pointer;
    & span {
        width: 78px;
        height: 28px;

        font-family: 'Inter';
        font-style: normal;
        font-weight: 500;
        font-size: 14px;
        line-height: 28px;
        display: flex;
        align-items: center;
        color: #f7f7f7;
        flex: none;
        order: 0;
        flex-grow: 0;
    }
`;
const StyledCheckbox = styled(Checkbox)`
    display: flex;
    align-items: center;
`;

const SearchWrap = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
`;
const SearchInput = styled.input`
    width: 1227px;
    padding-left: 20px;
    height: 33px;
    background: #ffffff;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.08);
    border-radius: 5px;

    border: none;
`;
const ButtonBlok = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
`;
const TableWrapper = styled.div`
    margin-top: 10px;

    .ant-table-thead {
        height: 50px;
        line-height: 50px;
        background: rgba(148, 129, 205, 0.25);
        box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
        border-radius: 5px 5px 0px 0px;
    }
    .ant-table-thead > tr > th::before {
        content: none !important;
    }
    .ant-table-cell {
        border-right: none !important;
        border-left: none !important;
        font-size: 14px !important;
    }

    .ant-table-row .ant-table-cell {
        height: 46px !important;
        font-family: 'Inter';
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 28px;
        align-items: center;
        color: #333333;

        & svg {
            margin-top: 9px;
        }
    }

    & td.leftAlign {
        text-align: left !important;
        padding-left: 5px !important;
    }

    & th.headerCenter {
        text-align: center !important;
    }
`;

const HeaderTitle = styled.div`
    margin-top: 38px;
    width: 110px;
    height: 28px;
    font-family: 'Inter';
    font-style: normal;
    font-weight: 600;
    font-size: 25px;
    line-height: 28px;
    align-items: center;
    color: #333333;
`;

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    min-height: 90%;
    top: 0px;
    gap: 16px;
    background-color: #f7f7f7;

    .ant-table-row {
        height: 14px !important;
        padding: 2px !important;
    }

    .ant-table-cell {
        height: 14px !important;
        padding: 2px !important;
    }

    .ant-table-tbody > tr > td {
        font-family: Inter;
        border: 1px solid #efefef;
        color: #333333;
        font-size: 12px;
        font-weight: 400;
        line-height: 12px;
        text-align: left;
        padding: 2px;
        height: 12px;
    }

    .ant-table-summary {
        background-color: #9481cd40;
        font-family: 'Inter';
        font-size: 14px;
        font-weight: 600;
        line-height: 16.8px;
        text-align: left;
    }

    .ant-table-thead > tr > th {
        background-color: #9481cd40;
        font-family: 'Inter';
        font-size: 12px;
        font-weight: 600;
        line-height: 13.2px;
        text-align: left;
    }
    .highlight-row {
        background-color: #f6f5eb !important;
    }
`;

const Header = styled.div`
    width: 100%;
    height: 40px;
    display: flex;
    justify-content: space-between;
    top: 0px;
    margin-bottom: 20px;
`;

const Overlay = styled.div<{
    display: string;
}>`
    display: ${({ display }) => display};
    position: fixed;
    top: 0;
    left: 0;
    z-index: 1000;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(1px);
`;
